import React, { useEffect, useState } from "react";
import axios from "axios";
import { X, Trash2, Loader2, Image as ImageIcon } from "lucide-react";
import { getToken } from "@/lib/api";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PUBLIC = process.env.REACT_APP_BACKEND_URL;

export const MediaLibrary = ({ open, onClose, onSelect }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/media`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      setItems(data.items || []);
    } catch (e) {
      toast.error("Could not load media.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
  }, [open]);

  const remove = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this image?")) return;
    try {
      await axios.delete(`${API}/media/${id}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      setItems((prev) => prev.filter((x) => x.id !== id));
      toast.success("Deleted.");
    } catch {
      toast.error("Delete failed.");
    }
  };

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[90] bg-midnight/95 backdrop-blur-md p-4 md:p-10 overflow-y-auto"
      data-testid="media-library-modal"
    >
      <div className="max-w-6xl mx-auto glass-strong p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="eyebrow">Media Library</div>
            <h2 className="font-display text-3xl text-ivory">Your uploads</h2>
          </div>
          <button onClick={onClose} className="text-ivory/70 hover:text-gold" data-testid="media-close">
            <X />
          </button>
        </div>

        {loading && (
          <div className="py-20 flex items-center justify-center text-ivory/55">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        )}

        {!loading && items.length === 0 && (
          <div className="py-20 text-center text-ivory/55">
            <ImageIcon className="h-10 w-10 mx-auto opacity-50 mb-3" />
            No images uploaded yet. Use the <b>Upload</b> button on any image field to add one.
          </div>
        )}

        <div className="grid gap-3 grid-cols-2 md:grid-cols-4 lg:grid-cols-5">
          {items.map((it) => (
            <button
              key={it.id}
              type="button"
              onClick={() => {
                onSelect(`${PUBLIC}${it.url}`);
                onClose();
              }}
              data-testid={`media-item-${it.id}`}
              className="group relative aspect-square overflow-hidden border border-gold/20 hover:border-gold transition"
            >
              <img src={`${PUBLIC}${it.url}`} alt={it.filename} className="h-full w-full object-cover" />
              <span
                onClick={(e) => remove(it.id, e)}
                className="absolute top-1 right-1 h-8 w-8 flex items-center justify-center bg-midnight/80 text-red-300 opacity-0 group-hover:opacity-100 transition"
              >
                <Trash2 className="h-4 w-4" />
              </span>
              <span className="absolute inset-x-0 bottom-0 bg-midnight/85 text-[10px] text-ivory/75 px-2 py-1 truncate">
                {it.filename}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MediaLibrary;
